// A postcode can also hold a development site or shops; the block of flats is the building to create.
export const pickReferenceCandidate = (candidates = []) =>
  candidates.find(
    candidate =>
      String(candidate?.buildingUprn || '').trim() &&
      Array.isArray(candidate?.childUprns) &&
      candidate.childUprns.length > 0
  ) ||
  candidates[0] ||
  null

// A building's own record is classed PP (property shell), which would suggest a single dwelling.
export const buildingTypeForTrustedBuilding = (
  lookupData,
  childClassificationCode = ''
) => {
  if (lookupData?.referenceData?.classificationCode !== 'PP') {
    return lookupData?.suggestedBuildingType || null
  }

  return String(childClassificationCode).toUpperCase().startsWith('RD')
    ? 'residential'
    : null
}

// Only trust the building's unit list when it is complete: it must hold every flat already seen on the searched postcode.
export const resolveBuildingUnitUprns = ({
  lookup,
  postcodeChildUprns = [],
  mapUprns = []
}) => {
  if (!lookup?.complete || !Array.isArray(lookup.uprns) || !lookup.uprns.length) {
    return null
  }

  const buildingUnits = new Set(lookup.uprns.map(uprn => String(uprn)))

  if (postcodeChildUprns.some(uprn => !buildingUnits.has(String(uprn)))) {
    return null
  }

  const unitUprns = mapUprns
    .map(uprn => String(uprn))
    .filter(uprn => buildingUnits.has(uprn))

  return unitUprns.length ? unitUprns : null
}

// The paid building lookup is only needed when the OS outline also holds units beyond this postcode's flats.
export const outlineHasOtherUnits = ({
  buildingUprn,
  childUprns = [],
  mapUprns = []
}) => {
  const knownUprns = new Set([String(buildingUprn), ...childUprns.map(String)])

  return mapUprns.some(uprn => !knownUprns.has(String(uprn)))
}

// Unit sync reuses the building's own units saved at creation instead of the wider OS outline.
export const narrowToSavedBuildingUnits = (savedUprns = [], outlineUprns = []) => {
  const outline = new Set(outlineUprns.map(String))
  const saved = savedUprns.map(String)

  return saved.length &&
    saved.length < outline.size &&
    saved.every(uprn => outline.has(uprn))
    ? saved
    : outlineUprns
}

export const initialBuildingUnits = bp => {
  const buildingUprn = String(bp?.uprn || '').trim()
  const selectedUprns = (bp?.selectedMapUprns || []).map(uprn => String(uprn))
  const outlineUprns = new Set(
    (bp?.selectedOsLinkedIdentifiers || []).flatMap(link =>
      (link?.uprns || []).map(uprn => String(uprn))
    )
  )

  return buildingUprn &&
    selectedUprns.length &&
    outlineUprns.size > selectedUprns.length
    ? { buildingUprn, uprns: selectedUprns }
    : null
}

export const resolveBlockOfFlatsReference = referenceDetails => {
  const buildingUprn = String(referenceDetails?.buildingUprn || '').trim()
  const childUprns = Array.isArray(referenceDetails?.childUprns)
    ? referenceDetails.childUprns.map(uprn => String(uprn))
    : []
  const isBlockOfFlats = Boolean(buildingUprn) && childUprns.length > 0

  return {
    isBlockOfFlats,
    buildingUprn: isBlockOfFlats ? buildingUprn : '',
    childUprns: isBlockOfFlats ? childUprns : []
  }
}
